import * as React from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Trash2,
  CornerDownRight,
  Download,
  ImagePlus,
} from "lucide-react";

interface ImageLightboxProps {
  url: string;
  onClose: () => void;
  onRemove: () => void;
  onPasteDown?: () => void;
  onAddImage: () => void;
}

// Fullscreen viewer for a note image. Rendered via a portal to document.body
// so it always covers the real viewport, regardless of any
// transformed/positioned ancestor (the note overlay's card uses a
// zoom-in-95 transform, which would otherwise break `fixed` positioning
// for anything nested inside it).
export function ImageLightbox({
  url,
  onClose,
  onRemove,
  onPasteDown,
  onAddImage,
}: ImageLightboxProps) {
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-black/95 animate-in fade-in duration-150"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <ArrowLeft className="h-5 w-5 text-white" />
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center overflow-hidden px-2">
        <img
          src={url}
          alt=""
          className="max-w-full max-h-full object-contain"
        />
      </div>

      <div className="shrink-0 px-4 pb-6 pt-2">
        <div className="flex items-center justify-center gap-1 rounded-full border border-white/10 bg-white/10 backdrop-blur-xl px-2 py-2 mx-auto w-fit flex-wrap">
          <button
            type="button"
            onClick={onRemove}
            className="flex items-center gap-1.5 h-9 px-4 rounded-full text-white text-sm font-medium hover:bg-white/10 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Remove
          </button>
          {onPasteDown && (
            <button
              type="button"
              onClick={onPasteDown}
              className="flex items-center gap-1.5 h-9 px-4 rounded-full text-white text-sm font-medium hover:bg-white/10 transition-colors"
            >
              <CornerDownRight className="h-4 w-4" />
              Paste down
            </button>
          )}
          <button
            type="button"
            onClick={onAddImage}
            className="flex items-center gap-1.5 h-9 px-4 rounded-full text-white text-sm font-medium hover:bg-white/10 transition-colors"
          >
            <ImagePlus className="h-4 w-4" />
            Add image
          </button>
          <a
            href={url}
            download
            className="flex items-center gap-1.5 h-9 px-4 rounded-full text-white text-sm font-medium hover:bg-white/10 transition-colors"
          >
            <Download className="h-4 w-4" />
            Download
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}
